import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { MechanicUpdateDocumentModel } from 'src/app/models/archive/mechanic-update-document.model';
import { MechanicModel } from 'src/app/models/archive/mechanic.model';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { MechanicImportModel } from 'src/app/models/imports/mechanic-import-model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MechanicService {

  constructor(private _httpClient: HttpClient) { }

  createMechanic(mechanicContractModel: MechanicModel): Observable<MechanicModel> {
    return this._httpClient.post<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.CreateMechanic}`,
      mechanicContractModel);
  }

  deleteMechanic(mechanicId: number): Observable<MechanicModel> {
    return this._httpClient.delete<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.DeleteMechanic}/${mechanicId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllMechanicsForSelectAsync(branchId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetAllMechanicsForSelectAsync}/${branchId}`,);
  }

  getAllMechanicsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetAllMechanicsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllTypeDocumentsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transporter.GetAllTypeDocumentsForSelectAsync}`,);
  }

  getDigitVerification(document: string): Observable<number> {
    const itemFilterResponse = new ItemFilterModel("document", null, null, "");
    itemFilterResponse.wildcard = document;
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetDigitVerification}`, itemFilterResponse);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetColumnsForGeneralConsultationAsync}`);
  }

  getMechanicById(mechanicId: number): Observable<MechanicModel> {
    return this._httpClient.get<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetMechanicById}/${mechanicId}`);
  }

  getMechanicByDocumentAsync(branchId: number, document: string): Observable<MechanicModel> {
    const itemFilterResponse = new ItemFilterModel("document", branchId, null, "");
    itemFilterResponse.wildcard = document;
    return this._httpClient.post<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetMechanicByDocumentAsync}`, itemFilterResponse)
  }

  getMechanicByName(branchId: number, name: string): Observable<MechanicModel> {
    const itemFilterResponse = new ItemFilterModel("name", branchId, null, "");
    itemFilterResponse.wildcard = name;
    return this._httpClient.post<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetMechanicByName}`, itemFilterResponse)
  }

  getMechanicsByBranchId(branchId: number): Observable<Array<MechanicModel>> {
    return this._httpClient.get<Array<MechanicModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.GetMechanicsByBranchId}/${branchId}`);
  }

  importMechanics(mechanicImports: Array<MechanicImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.ImportMechanic}`, mechanicImports);
  }

  setActiveMechanic(mechanicId: number): Observable<MechanicModel> {
    return this._httpClient.put<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.SetActiveMechanic}/${mechanicId}`,
      null);
  }

  updateMechanic(MechanicContract: MechanicModel): Observable<MechanicModel> {
    return this._httpClient.put<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.UpdateMechanic}`,
      MechanicContract);
  }

  updateMechanicDocument(MechanicContract: MechanicUpdateDocumentModel): Observable<MechanicModel> {
    return this._httpClient.put<MechanicModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.mechanic.UpdateMechanicDocument}`,
      MechanicContract);
  }
}
